import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabaseClient'
import { createVehicle, getAllBrands, getModelsByBrand, getOfferTypes, getFuelTypes } from '../services/vehicleCrudService'
import './AddVehicle.css'

const AddVehicle = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    brandId: '',
    modelId: '',
    year: new Date().getFullYear(),
    price: '',
    description: '',
    kilometers: '',
    power: '',
    offerTypeId: '',
    fuelTypeId: '',
    images: [''],
  })

  const [brands, setBrands] = useState([])
  const [models, setModels] = useState([])
  const [offerTypes, setOfferTypes] = useState([])
  const [fuelTypes, setFuelTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Cargar datos iniciales
  useEffect(() => {
    if (!user) {
      navigate('/')
      return
    }

    loadInitialData()
  }, [user, navigate])

  const loadInitialData = async () => {
    try {
      const [brandsData, offerTypesData, fuelTypesData] = await Promise.all([getAllBrands(), getOfferTypes(), getFuelTypes()])

      setBrands(brandsData)
      setOfferTypes(offerTypesData)
      setFuelTypes(fuelTypesData)
    } catch (err) {
      console.error('Error cargando datos:', err)
      setError('Error cargando los datos del formulario')
    }
  }

  // Cargar modelos cuando se selecciona una marca
  const handleBrandChange = async brandId => {
    setFormData({ ...formData, brandId, modelId: '' })
    setModels([])

    if (brandId) {
      try {
        const modelsData = await getModelsByBrand(brandId)
        setModels(modelsData)
      } catch (err) {
        console.error('Error cargando modelos:', err)
      }
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images]
    newImages[index] = value
    setFormData({ ...formData, images: newImages })

    // Limpiar error si había
    if (error && error.includes('URL duplicada')) {
      setError('')
    }
  }

  const addImageField = () => {
    // Verificar si hay URLs vacías antes de añadir una nueva
    const hasEmpty = formData.images.some(img => img.trim() === '')
    if (!hasEmpty) {
      setFormData({ ...formData, images: [...formData.images, ''] })
    }
  }

  const removeImageField = index => {
    const newImages = formData.images.filter((_, i) => i !== index)
    // Asegurar que siempre haya al menos un campo vacío
    if (newImages.length === 0) {
      newImages.push('')
    }
    setFormData({ ...formData, images: newImages })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Validar campos requeridos
      if (!formData.brandId || !formData.modelId || !formData.offerTypeId) {
        throw new Error('Por favor, completa todos los campos obligatorios (Marca, Modelo, Tipo de oferta)')
      }

      // Filtrar y validar imágenes
      const validImages = formData.images.map(img => img.trim()).filter(img => img !== '')

      // Verificar URLs duplicadas
      const uniqueImages = [...new Set(validImages)]
      if (uniqueImages.length < validImages.length) {
        throw new Error('⚠️ Tienes URLs de imágenes duplicadas. Elimina las duplicadas antes de continuar.')
      }

      // Validar que sean URLs válidas
      const invalidUrls = uniqueImages.filter(url => {
        try {
          new URL(url)
          return false
        } catch {
          return true
        }
      })

      if (invalidUrls.length > 0) {
        throw new Error(`⚠️ Algunas URLs no son válidas. Verifica que empiecen con http:// o https://`)
      }

      const vehicleData = {
        modelId: formData.modelId,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        description: formData.description,
        kilometers: parseInt(formData.kilometers),
        power: parseInt(formData.power),
        offerTypeId: formData.offerTypeId,
        fuelTypeId: formData.fuelTypeId,
        images: uniqueImages, // Usar URLs únicas y validadas
      }

      console.log('🆔 User ID que se enviará:', user.id)
      console.log('👤 User completo:', user)
      await createVehicle(vehicleData, user.id)

      alert('✅ Vehículo añadido correctamente')
      navigate('/mis-vehiculos')
    } catch (err) {
      console.error('Error creando vehículo:', err)
      setError(err.message || 'Error al crear el vehículo')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className='add-vehicle-page'>
      <div className='add-vehicle-container'>
        <div className='add-vehicle-header'>
          <button className='back-button' onClick={() => navigate('/')}>
            ← Volver
          </button>
          <h1>Añadir Nuevo Vehículo</h1>
        </div>

        {error && <div className='error-message'>{error}</div>}

        <form onSubmit={handleSubmit} className='vehicle-form'>
          {/* Marca y Modelo */}
          <div className='form-row'>
            <div className='form-group'>
              <label>
                Marca <span className='required'>*</span>
              </label>
              <select value={formData.brandId} onChange={e => handleBrandChange(e.target.value)} required>
                <option value=''>Selecciona una marca</option>
                {brands.map(brand => (
                  <option key={brand['marca_id(PK)']} value={brand['marca_id(PK)']}>
                    {brand.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-group'>
              <label>
                Modelo <span className='required'>*</span>
              </label>
              <select name='modelId' value={formData.modelId} onChange={handleChange} required disabled={!formData.brandId}>
                <option value=''>Selecciona un modelo</option>
                {models.map(model => (
                  <option key={model['model_id(PK)']} value={model['model_id(PK)']}>
                    {model.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tipo de Oferta y Combustible */}
          <div className='form-row'>
            <div className='form-group'>
              <label>
                Tipo de Oferta <span className='required'>*</span>
              </label>
              <select name='offerTypeId' value={formData.offerTypeId} onChange={handleChange} required>
                <option value=''>Selecciona el tipo</option>
                {offerTypes.map(type => (
                  <option key={type['tipus_oferta_id(PK)']} value={type['tipus_oferta_id(PK)']}>
                    {type.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-group'>
              <label>Tipo de Combustible</label>
              <select name='fuelTypeId' value={formData.fuelTypeId} onChange={handleChange}>
                <option value=''>Selecciona el combustible</option>
                {fuelTypes.map(fuel => (
                  <option key={fuel['tipus_combustible_id(PK)']} value={fuel['tipus_combustible_id(PK)']}>
                    {fuel.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Año, Precio, KM, Potencia */}
          <div className='form-row'>
            <div className='form-group'>
              <label>Año</label>
              <input type='number' name='year' value={formData.year} onChange={handleChange} min='1900' max={new Date().getFullYear() + 1} />
            </div>

            <div className='form-group'>
              <label>Precio (€)</label>
              <input type='number' name='price' value={formData.price} onChange={handleChange} min='0' step='0.01' />
            </div>

            <div className='form-group'>
              <label>Kilómetros</label>
              <input type='number' name='kilometers' value={formData.kilometers} onChange={handleChange} min='0' />
            </div>

            <div className='form-group'>
              <label>Potencia (CV)</label>
              <input type='number' name='power' value={formData.power} onChange={handleChange} min='0' />
            </div>
          </div>

          {/* Descripción */}
          <div className='form-group'>
            <label>Descripción</label>
            <textarea name='description' value={formData.description} onChange={handleChange} rows='4' placeholder='Describe tu vehículo...' />
          </div>

          {/* Imágenes */}
          <div className='form-group'>
            <label>Imágenes</label>

            <div className='urls-section'>
              <label style={{ color: '#61dafb', fontSize: '14px', fontWeight: '600' }}>Añadir URLs de imágenes</label>
              {formData.images.map((image, index) => (
                <div key={index} className='image-input-row'>
                  <input type='url' value={image} onChange={e => handleImageChange(index, e.target.value)} placeholder='https://images.unsplash.com/photo-ejemplo.jpg' />
                  {formData.images.length > 1 && (
                    <button type='button' className='remove-image-btn' onClick={() => removeImageField(index)}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type='button' className='add-image-btn' onClick={addImageField}>
                + Añadir otra URL
              </button>
            </div>

            {/* Preview de imágenes */}
            {formData.images.filter(img => img.trim() !== '').length > 0 && (
              <>
                <h4 style={{ color: '#61dafb', marginTop: '25px', marginBottom: '15px', fontSize: '16px' }}>Preview de imágenes ({formData.images.filter(img => img.trim() !== '').length})</h4>
                <div className='uploaded-images-grid'>
                  {formData.images.map((imageUrl, index) => {
                    if (imageUrl.trim() === '') return null

                    return (
                      <div key={index} className='uploaded-image-item'>
                        <img
                          src={imageUrl}
                          alt={`Preview ${index + 1}`}
                          onError={e => {
                            e.target.style.display = 'none'
                            const errorDiv = e.target.nextElementSibling
                            if (errorDiv) errorDiv.style.display = 'flex'
                          }}
                        />
                        <div
                          style={{
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            background: '#2a2a2a',
                            color: '#ff6b6b',
                            fontSize: '12px',
                            textAlign: 'center',
                            padding: '10px',
                            flexDirection: 'column',
                            gap: '5px',
                          }}
                        >
                          <span>⚠️</span>
                          <span>Error al cargar</span>
                          <span style={{ fontSize: '10px', color: '#999' }}>URL inválida o CORS</span>
                        </div>
                        <button type='button' className='remove-uploaded-btn' onClick={() => removeImageField(index)} title='Eliminar esta imagen'>
                          ✕
                        </button>
                        <span className='image-number'>#{formData.images.filter((img, i) => img.trim() !== '' && i <= index).length}</span>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>

          {/* Botones */}
          <div className='form-actions'>
            <button type='button' className='cancel-btn' onClick={() => navigate('/')} disabled={loading}>
              Cancelar
            </button>
            <button type='submit' className='submit-btn' disabled={loading}>
              {loading ? 'Guardando...' : 'Crear Vehículo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddVehicle
